import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

// region Libraries
import { Autocomplete, TextField } from "@libraries/mui";
import { atomWithReset, useAtom, useResetAtom } from "@libraries/jotai";
import { useAuth } from "@hooks/useAuth";
// endregion Libraries
// region Store
import Store from "@store/jotai";
// endregion Store
// region Graphql
import { ClientType } from "@store/graphql/helpers/types.graphql";
// endregion Graphql
// region components
import { FormattedPermissions } from "@pages/Office";
// endregion components
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

// endregion Languages
// region Styles
import * as Styled from "./styles";
// endregion Styles

// Instantiate the atom with a default value (Select client)
const selectClientAtom = atomWithReset<ClientType>({
  name: "",
  id: "00000000-0000-0000-0000-000000000000"
} as ClientType);

export function useSelectClient() {

  const [value, setValue] = useAtom(selectClientAtom);
  const reset = useResetAtom(selectClientAtom);

  return { value, setValue, reset };
}

interface SelectClientProps {
  showBorderBottom?: boolean;
  permissions?: FormattedPermissions;
}

const SelectClient: React.FC<SelectClientProps> = ({
  showBorderBottom = false,
  permissions = undefined
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { value: selectedClient, setValue: setSelectedClient } = useSelectClient();
  const { value: Clients } = Store.useClients();

  const filteredClients = useMemo(() => {
    if (user.admin || location.pathname === "/") {
      return Clients;
    }
    if (!permissions || !Clients.length) return [];

    return Clients.filter((client) => permissions[client.id]?.modules.includes(location.pathname));
  }, [Clients, permissions, location.pathname, user.admin]);

  useEffect(() => {
    if (filteredClients.length > 0 && selectedClient.id === "00000000-0000-0000-0000-000000000000") {
      setSelectedClient(filteredClients[0]);
    }
  }, [filteredClients, selectedClient.id, setSelectedClient]);

  /**
   * Handle change on select client
   */
  const handleChange = useCallback(
    (_event: any, value: any) => {
      setSelectedClient(value);
    },
    [setSelectedClient]
  );

  const dropdown = useMemo(() => (
    <Autocomplete
      disablePortal
      id="select-client"
      options={filteredClients}
      getOptionLabel={(option) => option.name}
      autoSelect
      disableClearable
      loading={Clients.length === 0}
      loadingText="Buscando dados..."
      value={selectedClient}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(GlobalMessages.selectClient)}
          variant="standard"
        />
      )}
    />
  ), [Clients, handleChange, selectedClient, t]);

  return (
    <Styled.Container showBorderBottom={showBorderBottom}>
      {dropdown}
    </Styled.Container>
  );
};

SelectClient.defaultProps = {
  showBorderBottom: false,
  permissions: {}
};

export default SelectClient;
